/**
 * The transactions table — shared by the hub, the per-estate history and the
 * estate page's recent-deals block (SEO G3).
 *
 * It lives in `shared/components` rather than inside the transactions feature
 * because TWO features render it: `storefront/transactions` and
 * `storefront/estates`. A cross-feature component is exactly what this
 * directory is for, and the alternative — a feature barrel — cannot satisfy
 * the deep-import ban on `@/features` subpaths, whose pattern predates the
 * `storefront/` grouping and so matches a storefront barrel too.
 *
 * ONE component because the row is the same fact everywhere: a deal has a
 * date, a mode, a price, an area, a per-foot figure, a floor band and a
 * source. Three renderers would be three places for a column to be dropped or
 * mislabelled.
 *
 * The `estate` column is the only variation, and it is driven by the data
 * rather than a prop: a row that carries an estate links to it, one that does
 * not (a statutory register entry whose development matched no estate) simply
 * has no link. On the per-estate page no row carries one, so the column
 * disappears on its own.
 *
 * Every figure is rendered from the server's exact integers — the per-foot
 * price is DERIVED ONCE on the backend, so this table never computes it and
 * cannot disagree with the estate page about the same deal.
 */
import { Link } from '@/shared/i18n/routing';
import { useTranslations } from '@/shared/i18n/useTranslations';
import type { TransactionRow } from '@/shared/lib/types/transactions';
import { formatHkd } from '@letsgethome/lib/currency';
import {
  floorLevelLabel,
  transactionModeLabel,
} from '@letsgethome/lib/proto-enums/listing/labels_gen';
import { marketDataSourceLabel } from '@letsgethome/lib/proto-enums/market/labels_gen';
import { routes } from '@letsgethome/routes';
import {
  cents,
  exactIntValue,
  plaintextString,
  slug,
  type Locale,
} from '@letsgethome/types/branded';
import { HorizontalScroller } from '@letsgethome/ui/horizontal-scroller';

export function TransactionsTable({
  rows,
  locale,
  showEstate,
}: {
  rows: readonly TransactionRow[];
  locale: Locale;
  /** Whether to render the estate column (the hub does; a single estate's
   *  history does not — every row would name the same estate). */
  showEstate: boolean;
}) {
  const t = useTranslations('transactions');

  return (
    // Wide tables scroll inside their own container — the page body must
    // never scroll horizontally.
    <HorizontalScroller className="rounded-lg border border-border">
      <table className="w-full min-w-max border-collapse text-sm">
        <thead className="bg-muted/50">
          <tr>
            <th scope="col" className="px-4 py-2 text-left font-medium">
              {t('date')}
            </th>
            {showEstate && (
              <th scope="col" className="px-4 py-2 text-left font-medium">
                {t('estate')}
              </th>
            )}
            <th scope="col" className="px-4 py-2 text-left font-medium">
              {t('kind')}
            </th>
            <th scope="col" className="px-4 py-2 text-right font-medium">
              {t('price')}
            </th>
            <th scope="col" className="px-4 py-2 text-right font-medium">
              {t('area')}
            </th>
            <th scope="col" className="px-4 py-2 text-right font-medium">
              {t('perSqft')}
            </th>
            <th scope="col" className="px-4 py-2 text-left font-medium">
              {t('floor')}
            </th>
            <th scope="col" className="px-4 py-2 text-left font-medium">
              {t('source')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-t border-border/60">
              <th scope="row" className="px-4 py-2 text-left font-normal">
                {/* An ISO date is data shown verbatim, not copy. */}
                {plaintextString(row.transactionDate)}
              </th>
              {showEstate && (
                <td className="px-4 py-2">
                  {row.estate ? (
                    <Link
                      // The COMPOSED `{slug}-{publicId}` segment — the same
                      // shape `buildEstateUrl` produces, kept in one place by
                      // the mapper that built the row.
                      href={routes.estates.detail(
                        slug(`${row.estate.slug}-${row.estate.publicId}`)
                      )}
                      prefetch="intent"
                      className="text-success-text underline-offset-2 hover:underline"
                    >
                      {plaintextString(row.estate.name)}
                    </Link>
                  ) : (
                    // A register entry whose development matched no estate.
                    // Real data with nothing to link to — never a guess.
                    <span className="text-muted-foreground">{t('unlinkedEstate')}</span>
                  )}
                </td>
              )}
              <td className="px-4 py-2">{transactionModeLabel(row.kind, locale)}</td>
              <td className="px-4 py-2 text-right tabular-nums">
                {/* `ExactInt` → bigint → the number `formatHkd` takes. The decimal-string
                    carrier is what lets this row survive `JSON.stringify` at all. */}
                {formatHkd(cents(Number(exactIntValue(row.priceCents))))}
              </td>
              <td className="px-4 py-2 text-right tabular-nums">
                {row.areaSqft === undefined ? (
                  // The source recorded no area. An absence, rendered as one.
                  <span className="text-muted-foreground">—</span>
                ) : (
                  t('sqft', { area: row.areaSqft })
                )}
              </td>
              <td className="px-4 py-2 text-right tabular-nums">
                {row.pricePerSqftCents === undefined ? (
                  // No area means no honest per-foot figure — never an
                  // estimate.
                  <span className="text-muted-foreground">—</span>
                ) : (
                  formatHkd(cents(Number(exactIntValue(row.pricePerSqftCents))))
                )}
              </td>
              <td className="px-4 py-2">
                {row.floorBand === undefined ? (
                  <span className="text-muted-foreground">—</span>
                ) : (
                  floorLevelLabel(row.floorBand, locale)
                )}
              </td>
              {/* Attribution on every row: an own-platform deal and a
                  statutory register entry are different kinds of evidence. */}
              <td className="px-4 py-2 text-muted-foreground">
                {marketDataSourceLabel(row.source, locale)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </HorizontalScroller>
  );
}
